import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/PresenzaStaffForm.css";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

const API_URL = process.env.REACT_APP_API_URL;

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString("it-IT");
  } catch {
    return value;
  }
}

function formatAllenamentoLabel(allenamento) {
  if (!allenamento) return "-";

  const data = allenamento.dataAllenamento
    ? formatDate(allenamento.dataAllenamento)
    : "-";

  const oraInizio = allenamento.oraInizio || "";
  const oraFine = allenamento.oraFine || "";
  const campo = allenamento.campo || "";

  let label = data;
  if (oraInizio) {
    label += ` - ${oraInizio}`;
    if (oraFine) label += ` / ${oraFine}`;
  }
  if (campo) {
    label += ` - ${campo}`;
  }

  return label;
}

export default function PresenzaStaffForm() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [categorie, setCategorie] = useState([]);
  const [categoriaSelezionataId, setCategoriaSelezionataId] = useState("");
  const [allenamenti, setAllenamenti] = useState([]);
  const [idAllenamentoSelezionato, setIdAllenamentoSelezionato] = useState("");
  const [presenze, setPresenze] = useState([]);

  const [loadingCategorie, setLoadingCategorie] = useState(false);
  const [loadingAllenamenti, setLoadingAllenamenti] = useState(false);
  const [loadingPresenze, setLoadingPresenze] = useState(false);
  const [saving, setSaving] = useState(false);

  const parseErrorMessage = async (res, fallbackMessage) => {
    try {
      const contentType = res.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        const data = await res.json();
        return data?.message || data?.error || data?.details || fallbackMessage;
      }

      const text = await res.text();
      return text || fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  };

  const fetchCategorie = useCallback(async () => {
    setLoadingCategorie(true);
    try {
      const res = await authFetch(`${API_URL}/api/allenamenti/mie-categorie`);

      if (!res.ok) {
        throw new Error(await parseErrorMessage(res, "Errore caricamento categorie staff"));
      }

      const data = await res.json().catch(() => []);
      const lista = Array.isArray(data) ? data : [];

      setCategorie(lista);

      if (lista.length === 1) {
        setCategoriaSelezionataId(String(lista[0].id));
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Errore caricamento categorie staff");
      setCategorie([]);
    } finally {
      setLoadingCategorie(false);
    }
  }, [authFetch]);

  const fetchAllenamenti = useCallback(
    async (idCategoria) => {
      if (!idCategoria) {
        setAllenamenti([]);
        setIdAllenamentoSelezionato("");
        setPresenze([]);
        return;
      }

      setLoadingAllenamenti(true);
      try {
        const res = await authFetch(`${API_URL}/api/allenamenti/categoria/${idCategoria}`);

        if (!res.ok) {
          throw new Error(await parseErrorMessage(res, "Errore caricamento allenamenti"));
        }

        const data = await res.json().catch(() => []);
        const lista = Array.isArray(data) ? data : [];

        const oggi = new Date();
        oggi.setHours(0, 0, 0, 0);

        const futuri = lista.filter((a) => {
          if (!a?.dataAllenamento) return false;
          const dataAllenamento = new Date(a.dataAllenamento);
          dataAllenamento.setHours(0, 0, 0, 0);
          return dataAllenamento >= oggi;
        });

        setAllenamenti(futuri);
        setIdAllenamentoSelezionato("");
        setPresenze([]);
      } catch (err) {
        console.error(err);
        toast.error(err.message || "Errore caricamento allenamenti");
        setAllenamenti([]);
        setIdAllenamentoSelezionato("");
        setPresenze([]);
      } finally {
        setLoadingAllenamenti(false);
      }
    },
    [authFetch]
  );

  const fetchPresenze = useCallback(
    async (idAllenamento) => {
      if (!idAllenamento) {
        setPresenze([]);
        return;
      }

      setLoadingPresenze(true);
      try {
        const res = await authFetch(
          `${API_URL}/api/admin/presenze/allenamento/${idAllenamento}`
        );

        if (!res.ok) {
          throw new Error(await parseErrorMessage(res, "Errore caricamento presenze"));
        }

        const data = await res.json().catch(() => []);
        const lista = Array.isArray(data) ? data : [];

        setPresenze(lista);
      } catch (err) {
        console.error(err);
        toast.error(err.message || "Errore caricamento presenze");
        setPresenze([]);
      } finally {
        setLoadingPresenze(false);
      }
    },
    [authFetch]
  );

  useEffect(() => {
    fetchCategorie();
  }, [fetchCategorie]);

  useEffect(() => {
    if (!categoriaSelezionataId) {
      setAllenamenti([]);
      setIdAllenamentoSelezionato("");
      setPresenze([]);
      return;
    }

    fetchAllenamenti(categoriaSelezionataId);
  }, [categoriaSelezionataId, fetchAllenamenti]);

  useEffect(() => {
    if (!idAllenamentoSelezionato) {
      setPresenze([]);
      return;
    }

    fetchPresenze(idAllenamentoSelezionato);
  }, [idAllenamentoSelezionato, fetchPresenze]);

  const togglePresenza = (idPresenza) => {
  setPresenze((prev) =>
    prev.map((p) =>
      p.id === idPresenza
        ? { ...p, presente: !p.presente }
        : p
    )
  );
};

  const handleSubmit = async () => {
  if (!categoriaSelezionataId) {
    toast.warning("Seleziona una categoria.");
    return;
  }

  if (!idAllenamentoSelezionato) {
    toast.warning("Seleziona un allenamento.");
    return;
  }

  if (presenze.length === 0) {
    toast.warning("Nessuna presenza da salvare.");
    return;
  }

  setSaving(true);

  try {
    const items = presenze.map((p) => ({
      idGiocatore: Number(p.idGiocatore),
      presente: !!p.presente,
    }));

    const hasInvalidItems = items.some(
      (item) => !item.idGiocatore || Number.isNaN(item.idGiocatore)
    );

    if (hasInvalidItems) {
      throw new Error("Alcune righe presenza non hanno idGiocatore valido");
    }

    const payload = {
      idAllenamento: Number(idAllenamentoSelezionato),
      items,
    };

    const res = await authFetch(`${API_URL}/api/admin/presenze/batch`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error(await parseErrorMessage(res, "Errore salvataggio presenze"));
    }

    toast.success("Presenze salvate correttamente");
    fetchPresenze(idAllenamentoSelezionato);
  } catch (err) {
    console.error(err);
    toast.error(err.message || "Errore comunicazione server");
  } finally {
    setSaving(false);
  }
};

  const columns = useMemo(
  () => [
    { accessorKey: "cognomeGiocatore", header: "Cognome" },
    { accessorKey: "nomeGiocatore", header: "Nome" },
    {
      id: "presente",
      header: "Presente",
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={!!row.original.presente}
          onChange={() => togglePresenza(row.original.id)}
          disabled={saving}
        />
      ),
    },
  ],
  [saving]
);

  const table = useReactTable({
    data: presenze,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="presenze-container" translate="no">
      <h2>Presenze Allenamento (Staff)</h2>

      <div className="presenze-row">
        <label>Categoria</label>
        <select
          value={categoriaSelezionataId}
          onChange={(e) => setCategoriaSelezionataId(e.target.value)}
          disabled={loadingCategorie || saving}
        >
          <option value="">
            {loadingCategorie ? "Caricamento categorie..." : "Seleziona categoria"}
          </option>
          {categorie.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.nome}
            </option>
          ))}
        </select>
      </div>

      {allenamenti.length > 0 && (
        <div className="presenze-row">
          <label>Allenamento</label>
          <select
            value={idAllenamentoSelezionato}
            onChange={(e) => setIdAllenamentoSelezionato(e.target.value)}
            disabled={loadingAllenamenti || saving}
          >
            <option value="">
              {loadingAllenamenti ? "Caricamento allenamenti..." : "Seleziona allenamento"}
            </option>
            {allenamenti.map((a) => (
  <option key={a.id} value={a.id}>
    {formatAllenamentoLabel(a)}
  </option>
))}
          </select>
        </div>
      )}

      {(loadingCategorie || loadingAllenamenti || loadingPresenze) && (
        <div className="presenze-info-box">Caricamento in corso...</div>
      )}

      {!loadingPresenze && idAllenamentoSelezionato && presenze.length === 0 && (
        <div className="presenze-info-box">
          Nessuna presenza disponibile per l’allenamento selezionato.
        </div>
      )}

      {presenze.length > 0 && idAllenamentoSelezionato && (
        <>
          <div className="presenze-tabella-container">
            <table className="presenze-tabella">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((h) => (
                      <th key={h.id}>
                        {flexRender(h.column.columnDef.header, h.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="presenze-button-row">
            <button
              className="presenze-btn-salva"
              type="button"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? "Salvataggio..." : "Salva Presenze"}
            </button>

            <button
              className="presenze-btn-torna"
              type="button"
              onClick={() => navigate("/gestionale1")}
              disabled={saving}
            >
              Torna al Gestionale
            </button>
          </div>
        </>
      )}
    </div>
  );
}